import React, { useContext, useState } from 'react';
import {
  IHistoryRecalc,
  IRecalcOperator,
  useCreateHistoryRecalcMutation,
  useGetRecalcOperatorsQuery,
  useUpdateHistoryRecalcMutation
} from '../../../../generated/graphql';
import DialogBox from '../../../Common/Layout/DialogBox';
import { Box } from '@mui/material';
import EditButton from '../../../Common/Buttons/EditButton';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import { canUserEdit } from '../../../../utils/rightUtils';
import { Colors } from '../../../../utils/theme';
import CancelButton from '../../../Common/Buttons/CancelButton';
import SaveButton from '../../../Common/Buttons/SaveButton';
import AutocompleteComponent from '../../../Common/Fields/AutocompleteComponent';
import InputComponent from '../../../Common/Fields/InputComponent';
import { parseDate } from '../../../../utils/clientUtils';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../../../Common/Fields/EpraDatePicker';
import moment from 'moment';
import { DATE_TIME_FORMATS } from '../../../../utils/constants';

interface IProps {
  stockId: string;
  handleClose: (saved: boolean, isNew?: boolean, historyRecalcInput?: IHistoryRecalc) => void;
  historyRecalcInput: IHistoryRecalc | null;
}

type IErrors = {
  startDate: string | null;
  endDate: string | null;
  operator: string | null;
  field: string | null;
  factor: string | null;
  endDateAfterStart: string | null;
};

const defaultErrorsObj: IErrors = {
  startDate: null,
  endDate: null,
  operator: null,
  field: null,
  factor: null,
  endDateAfterStart: null
};

const ErrorTexts: IErrors = {
  startDate: 'Start date required!',
  endDate: 'End date required!',
  operator: 'Operator is required',
  field: 'Please select data to recalculate',
  factor: 'Factor field is required',
  endDateAfterStart: 'End date should be after start date'
};

const RecalcForm: React.FunctionComponent<IProps> = ({
  stockId,
  handleClose,
  historyRecalcInput
}: IProps) => {
  const [startDate, setStartDate] = useState<Date | null>(
    historyRecalcInput ? parseDate(historyRecalcInput.start_date) : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    historyRecalcInput ? parseDate(historyRecalcInput.end_date) : null
  );
  const [field, setField] = useState(historyRecalcInput?.column_to_recalc || null);
  const [factor, setFactor] = useState(historyRecalcInput?.value_to_calc_with || null);
  const [operator, setOperator] = useState(historyRecalcInput?.operator || null);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [updateRecalc, updateRecalcResult] = useUpdateHistoryRecalcMutation();
  const [editable, setEditable] = useState(historyRecalcInput === null);
  const [createdRecalc, createdRecalcResult] = useCreateHistoryRecalcMutation();
  const recalcOperators = useGetRecalcOperatorsQuery();
  const { dispatch, state } = useContext(AppContext);
  const canEdit = canUserEdit(state.user, Types.Stock);

  const handleCancel = () => {
    if (editable && historyRecalcInput !== null) {
      setStartDate(parseDate(historyRecalcInput.start_date));
      setEndDate(parseDate(historyRecalcInput.end_date));
      setFactor(historyRecalcInput?.value_to_calc_with || null);
      setField(historyRecalcInput?.column_to_recalc || null);
      setOperator(historyRecalcInput?.operator || null);
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    } else {
      handleClose(false);
    }
  };

  const handleStartDateChange = (dateInput: Date | null) => {
    let errors = formErrors;
    errors = { ...errors, startDate: null };
    if (dateInput) {
      if (endDate) {
        if (endDate < dateInput) {
          errors = { ...errors, endDate: ErrorTexts.endDateAfterStart };
        } else {
          errors = { ...errors, endDate: null };
        }
      }
    } else {
      errors = { ...errors, startDate: ErrorTexts.startDate };
    }
    setFormErrors(errors);
    setStartDate(dateInput);
  };

  const handleEndDateChange = (dateInput: Date | null) => {
    let errors = formErrors;
    errors = { ...errors, endDate: null };
    if (dateInput) {
      if (startDate) {
        if (dateInput.getTime() < startDate.getTime()) {
          errors = { ...errors, endDate: ErrorTexts.endDateAfterStart };
        }
      }
    } else {
      errors = { ...errors, endDate: ErrorTexts.endDate };
    }
    setFormErrors(errors);
    setEndDate(dateInput);
  };

  const handleFieldChange = (_: unknown, newValue: string | null) => {
    setFormErrors({ ...formErrors, field: newValue === null ? ErrorTexts.field : null });
    setField(newValue);
  };

  const handleOperatorChange = (_: unknown, newValue: IRecalcOperator | null) => {
    setFormErrors({ ...formErrors, operator: newValue === null ? ErrorTexts.operator : null });
    setOperator(newValue);
  };

  const handleFactorChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const value = e.currentTarget.value === '' ? null : Number(e.currentTarget.value);
    setFormErrors({ ...formErrors, factor: value ? null : ErrorTexts.factor });
    setFactor(value);
  };

  const handleSubmit = () => {
    let hasErrors = false;
    const errors: IErrors = {
      endDate: null,
      endDateAfterStart: null,
      factor: null,
      field: null,
      operator: null,
      startDate: null
    };
    if (!endDate) {
      hasErrors = true;
      errors.endDate = ErrorTexts.endDate;
    }
    if (!startDate) {
      hasErrors = true;
      errors.startDate = ErrorTexts.startDate;
    }
    if (startDate && endDate) {
      if (startDate > endDate) {
        hasErrors = true;
        errors.startDate = ErrorTexts.endDateAfterStart;
        errors.endDate = ErrorTexts.endDateAfterStart;
      }
    }
    if (!factor) {
      hasErrors = true;
      errors.factor = ErrorTexts.factor;
    }
    if (!field) {
      hasErrors = true;
      errors.field = ErrorTexts.field;
    }
    if (!operator) {
      hasErrors = true;
      errors.operator = ErrorTexts.operator;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      handleSave(historyRecalcInput === null ? 'create' : 'update');
    }
  };

  const handleSave = (type: 'create' | 'update') => {
    if (type === 'create') {
      createdRecalc({
        variables: {
          stock_id: stockId,
          input: {
            column_to_recalc: field as string,
            operator_id: Number(operator?.id),
            end_date: moment(endDate).utc(true).format(DATE_TIME_FORMATS.DATE_DASHED),
            start_date: moment(startDate).utc(true).format(DATE_TIME_FORMATS.DATE_DASHED),
            value_to_calc_with: Number(factor)
          }
        },
        update(_, { data }) {
          handleClose(true, true, data?.result as IHistoryRecalc);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Recalc created'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    } else {
      updateRecalc({
        variables: {
          id: historyRecalcInput?.id as string,
          stock_id: stockId,
          input: {
            column_to_recalc: field as string,
            end_date: moment(endDate).utc(true).format(DATE_TIME_FORMATS.DATE_DASHED),
            start_date: moment(startDate).utc(true).format(DATE_TIME_FORMATS.DATE_DASHED),
            operator_id: Number(operator?.id),
            value_to_calc_with: Number(factor)
          }
        },
        update(_, { data }) {
          handleClose(true, false, data?.result as IHistoryRecalc);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Recalc Updated'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    }
  };

  return (
    <DialogBox
      id="history-recalc-dialog"
      width={410}
      title={historyRecalcInput ? 'Recalc Details' : 'Create Recalc'}
      onClose={handleClose}
      childrenSx={{ px: '40px', py: '20px' }}
    >
      {historyRecalcInput && (
        <Box sx={{ textAlign: 'right', width: '100%' }}>
          <EditButton
            id="allocation-country-form-edit-button"
            onClick={() => setEditable(true)}
            disabled={editable || !canEdit}
            editButtonColor={editable ? Colors.gray : Colors.skyBlue}
          />
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          mt: '20px',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '24px'
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <EpraDatePicker
            id="history-recalc-start-date-picker"
            label="Start Date"
            value={startDate}
            {...(endDate && { maxDate: endDate })}
            disabled={createdRecalcResult.loading || updateRecalcResult.loading || !editable}
            onChange={(d) => handleStartDateChange(d)}
            errorMsg={formErrors.startDate}
          />
          <EpraDatePicker
            id="history-recalc-end-date-picker"
            label="End Date"
            value={endDate}
            {...(startDate && { minDate: startDate })}
            disabled={createdRecalcResult.loading || updateRecalcResult.loading || !editable}
            onChange={(d) => handleEndDateChange(d)}
            errorMsg={formErrors.endDate}
          />
        </LocalizationProvider>

        <AutocompleteComponent
          id="recalc-field-autocomplete"
          data-testid="recalc-field-autocomplete"
          options={['div_value', 'nav_epra', 'close_price', 'epra_shares']}
          label="Field"
          values={field}
          onChange={handleFieldChange}
          disabled={
            createdRecalcResult.loading ||
            updateRecalcResult.loading ||
            !editable ||
            historyRecalcInput !== null
          }
          errorMsg={formErrors.field}
          additionalStyle={{ gridColumn: 'span 2' }}
        />
        <AutocompleteComponent
          id="recalc-operator-autocomplete"
          data-testid="recalc-operator-autocomplete"
          options={(recalcOperators.data?.result as never[]) || []}
          getOptionLabel={(option: IRecalcOperator) => option?.value}
          label="Operator"
          values={operator}
          onChange={handleOperatorChange}
          disabled={createdRecalcResult.loading || updateRecalcResult.loading || !editable}
          errorMsg={formErrors.operator}
        />
        <InputComponent
          id="recalc-form-factor-value"
          data-testid="recalc-form-factor-value"
          label="Factor"
          value={factor || ''}
          onChange={(e) => handleFactorChange(e)}
          type="number"
          disabled={createdRecalcResult.loading || updateRecalcResult.loading || !editable}
          errorMsg={formErrors.factor}
        />
      </Box>
      <Box
        className="centralize"
        sx={{
          gridColumn: 'span 3',
          marginTop: '50px',
          paddingBottom: '45px'
        }}
      >
        <CancelButton
          id="cancel-history-recalc-button"
          onClick={handleCancel}
          disabled={createdRecalcResult.loading || updateRecalcResult.loading}
          text={historyRecalcInput === null || editable ? 'Cancel' : 'Close'}
        />
        <SaveButton
          id="save-allocation-country-button"
          onClick={handleSubmit}
          disabled={createdRecalcResult.loading || updateRecalcResult.loading || !editable}
          text={historyRecalcInput === null ? 'Create' : 'Save'}
        />
      </Box>
    </DialogBox>
  );
};

export default RecalcForm;
